import { useMutation, useQueryClient } from '@tanstack/react-query';

import { schedulerApi } from '../api';
import { Routine } from '@demind-inc/core';
import { cloneDeep } from 'lodash';
import { FirestoreRoutineActivity } from '../types';
import { trackEventMixpanel } from '../../utils';
import { eventsSnackBarAtom } from '../recoil/atoms';
import { useRecoilState } from 'recoil';
import { translateI18n } from '../../i18n';

interface UseUpdateRoutineParams {
  routineId: string;
  activityId: string;
  preferenceId: string;
  routineActivity: Partial<FirestoreRoutineActivity>;
}

export const useUpdateRoutine = () => {
  const queryClient = useQueryClient();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const updateRoutineMutation = useMutation({
    mutationFn: ({
      routineId,
      activityId,
      preferenceId,
      routineActivity,
    }: UseUpdateRoutineParams) => {
      return schedulerApi
        .updateRoutineActivity(routineId, activityId, routineActivity, preferenceId)
        .then(({ data }) => data);
    },
    onMutate: async ({ routineId, activityId, routineActivity }: UseUpdateRoutineParams) => {
      setEventsSnackbar({
        message: translateI18n('routine.snackbar.updating'),
        autoHideDuration: 'disable',
        type: 'loading',
      });
      await queryClient.cancelQueries({
        queryKey: ['lifestack.scheduler.routine', { routineId }],
      });
      queryClient.setQueriesData<Routine>(
        { queryKey: ['lifestack.scheduler.routine', { routineId }] },
        (prev) => {
          const newActivities = cloneDeep(prev?.routineActivities ?? []).map((activity) => {
            if (activity.routineActivityId === activityId) {
              return { ...activity, ...routineActivity };
            }
            return activity;
          });

          return { ...prev, routineActivities: newActivities } as Routine;
        }
      );
    },
    onSuccess: (_, { routineId, activityId, routineActivity }) => {
      setEventsSnackbar({ message: translateI18n('routine.snackbar.updated'), type: 'success' });
      trackEventMixpanel('update_routine', {
        routineId,
        activityId,
        name: routineActivity.name,
      });
      queryClient.invalidateQueries({ queryKey: ['lifestack.scheduler.routine', { routineId }] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
    },
    onError: () => {
      setEventsSnackbar({ message: translateI18n('routine.snackbar.updateFailed'), type: 'error' });
    },
  });

  return {
    updateRoutine: updateRoutineMutation.mutateAsync,
    ...updateRoutineMutation,
  };
};
